import { DateTime as PlainDateTime, Duration } from "luxon";
import type { BusinessDayNumbers, HolidayMatcher } from "luxon-business-days";
import { DateTime } from "luxon-business-days";

import type { AggregatedTimesheet } from "@miter/types/aggregations";
import type { WeekStartDay } from "@miter/types/common";
import type { Company } from "@miter/types/models";

/** Luxon's weekday numbering: 1 = Monday ... 7 = Sunday. */
export const LUXON_WEEKDAY_BY_WEEK_START_DAY: Record<WeekStartDay, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

/**
 * Configures a DateTime instance with standard US business days (weekdays & holidays)
 * @param dt The DateTime instance to configure
 * @returns The configured DateTime instance
 */
export const configureBusinessCalendar = (dt: DateTime): DateTime => {
  // Get the holiday matchers directly from the dt instance
  const { availableHolidayMatchers } = dt;

  const {
    isNewYearsDay,
    isMLKDay,
    isEasterDay,
    isMemorialDay,
    isIndependanceDay,
    isLaborDay,
    isColumbusDay,
    isThanksgivingDay,
    isChristmasDay,
  } = availableHolidayMatchers;

  const holidays = [
    isNewYearsDay,
    isMLKDay,
    isEasterDay,
    isMemorialDay,
    isIndependanceDay,
    isLaborDay,
    isColumbusDay,
    isThanksgivingDay,
    isChristmasDay,
  ].filter(Boolean) as HolidayMatcher[]; // Filter out undefined matchers

  const weekdays: BusinessDayNumbers[] = [1, 2, 3, 4, 5]; // Monday to Friday

  dt.setupBusiness({ holidayMatchers: holidays, businessDays: weekdays });

  return dt;
};

/**
 * Checks if current date is more than a specified number of business days after a given date
 * @param date The reference date in ISO format
 * @param days The number of business days to check (default: 3)
 * @returns True if today is more than the specified business days after the reference date
 */
export const isMoreThanBusinessDaysAfter = (date: string, days: number = 3): boolean => {
  if (!date) return false;

  // Convert date to DateTime and configure business calendar
  const refDate = configureBusinessCalendar(DateTime.fromISO(date));

  // Add specified business days to reference date
  const deadlineDate = refDate.plusBusiness({ days });

  // Get current date with business calendar
  const today = configureBusinessCalendar(DateTime.local());

  // Return true if today is after the deadline
  return today > deadlineDate;
};

/**
 * Gets the number of business days between a reference date and today
 * @param refDate The reference date in ISO format
 * @returns The number of business days since the reference date
 */
export const getBusinessDaysSince = (refDate: string): number => {
  if (!refDate) return 0;

  // Convert dates to DateTime objects with business calendar
  const start = configureBusinessCalendar(DateTime.fromISO(refDate));
  const end = configureBusinessCalendar(DateTime.local());

  // If end date is before or equal to start date, return 0
  if (end <= start) return 0;

  // Count business days between start and end dates
  let count = 0;
  let current = start.plus({ days: 1 }); // Start from the day after reference date

  // Iterate through each day and count business days
  while (current <= end) {
    if (current.isBusinessDay()) {
      count++;
    }
    current = current.plus({ days: 1 });
  }

  return count;
};

/**
 * Gets the number of business days between two dates
 * @param startDate The start date in ISO format
 * @param endDate The end date in ISO format (defaults to today if not provided)
 * @returns The number of business days between the two dates
 */
export const getBusinessDaysBetween = (startDate: string, endDate?: string): number => {
  if (!startDate) return 0;

  // Convert start date to DateTime with business calendar
  const start = configureBusinessCalendar(DateTime.fromISO(startDate));

  // Use provided end date or today
  const end = endDate
    ? configureBusinessCalendar(DateTime.fromISO(endDate))
    : configureBusinessCalendar(DateTime.local());

  // If end date is before or equal to start date, return 0
  if (end <= start) return 0;

  // Count business days between start and end dates
  let count = 0;
  let current = start.plus({ days: 1 }); // Start from the day after start date

  // Iterate through each day and count business days
  while (current <= end) {
    if (current.isBusinessDay()) {
      count++;
    }
    current = current.plus({ days: 1 });
  }

  return count;
};

/**
 * Formats a Unix timestamp (in seconds) to a readable date string
 * @param unixTimestamp The Unix timestamp in seconds
 * @param format The format to use (default: "MMM dd, yyyy")
 * @returns A formatted date string
 */
export const formatUnixTimestamp = (
  unixTimestamp?: number | string,
  format: string = "MMM dd, yyyy"
): string => {
  if (!unixTimestamp) return "-";

  try {
    // Convert to number if it's a string
    const timestamp = typeof unixTimestamp === "string" ? parseFloat(unixTimestamp) : unixTimestamp;

    // Convert seconds to milliseconds for JavaScript Date
    return DateTime.fromSeconds(timestamp).toFormat(format);
  } catch {
    return String(unixTimestamp);
  }
};

/**
 * Gets a date that is N business days from the given date (or today if no date provided)
 * @param days The number of business days to add
 * @param fromDate The starting date in ISO format (defaults to today if not provided)
 * @returns The resulting date as a DateTime object
 */
const getDatePlusBusinessDays = (days: number, fromDate?: string): DateTime => {
  const start = fromDate
    ? configureBusinessCalendar(DateTime.fromISO(fromDate))
    : configureBusinessCalendar(DateTime.local());

  return start.plusBusiness({ days });
};

/**
 * Gets a formatted date string that is N business days from today
 * @param days The number of business days to add
 * @param format The format to use (default: "MMMM d, yyyy" e.g. "January 20, 2026")
 * @returns A formatted date string
 */
export const getFormattedDatePlusBusinessDays = (days: number, format: string = "MMMM d, yyyy"): string => {
  return getDatePlusBusinessDays(days).toFormat(format);
};

/**
 * Gets the ISO date (yyyy-mm-dd) that is N business days before the given date, using the US
 * business calendar (weekdays minus holidays).
 * @param fromDate The reference date in ISO format
 * @param days The number of business days to subtract
 * @returns The resulting date in ISO format, or null if the input is invalid
 */
export const getDateMinusBusinessDays = (fromDate: string, days: number): string | null =>
  configureBusinessCalendar(DateTime.fromISO(fromDate)).minusBusiness({ days }).toISODate();

/**
 * Gets the previous business day
 * @returns The previous business day in ISO format or null if no previous business day exists
 */
export const getPreviousBusinessDay = (date?: PlainDateTime): string | null => {
  let businessCalendarDate: DateTime | undefined;

  if (date) {
    // Convert regular DateTime to luxon-business-days DateTime
    const isoDate = date.toISODate();

    if (isoDate) {
      businessCalendarDate = configureBusinessCalendar(DateTime.fromISO(isoDate));
    }
  } else {
    businessCalendarDate = configureBusinessCalendar(DateTime.local());
  }

  if (!businessCalendarDate) {
    return null;
  }

  const previousBusinessDay = businessCalendarDate.minusBusiness({ days: 1 });

  return previousBusinessDay.toISODate();
};

export const todayET = (): string => PlainDateTime.now().setZone("America/New_York").toISODate();

export const addDays = (isoDate: string, days: number): string =>
  PlainDateTime.fromISO(isoDate).plus({ days }).toISODate();

export const daysBetweenDates = (from: string, to: string): number =>
  PlainDateTime.fromISO(to).diff(PlainDateTime.fromISO(from), "days").days;

export const formatDateString = (isoDate: string, format = "MMM d, yyyy"): string =>
  PlainDateTime.fromISO(isoDate).toFormat(format);

export const formatEndDateString = (isoDate: string, format = "MMM d, yyyy"): string =>
  PlainDateTime.fromISO(isoDate).minus({ days: 1 }).toFormat(format);

/** Converts exclusive end date to inclusive display date (subtracts 1 day) */
export const exclusiveToInclusiveDate = (isoDate: string): string =>
  PlainDateTime.fromISO(isoDate).minus({ days: 1 }).toISODate();

const SECONDS_PER_HOUR = Duration.fromObject({ hours: 1 }).as("seconds");

type GetClockInAndOutCompany = Pick<Company, "timezone"> & {
  settings: {
    timesheets: {
      default_clock_in_time?: string;
    };
  };
};

type GetClockInAndOutTimesheet = Pick<AggregatedTimesheet, "clock_in">;

/**
 * Calculates clock in and clock out timestamps based on hours and date
 * @param inputs Object containing hours, date, company settings, and optional timesheet/break data
 * @returns Object with clock_in and clock_out timestamps in seconds
 */
export const getClockInAndOut = (inputs: {
  hours: number;
  date: string;
  company: GetClockInAndOutCompany | null;
  unpaidBreakSeconds?: number | null;
  dayElapsedHours?: number;
  ts?: GetClockInAndOutTimesheet;
  /** Policy-resolved "HH:mm" override; falls back to the company setting when absent. */
  defaultClockInTime?: string | null;
}): { clock_in: number; clock_out: number } => {
  const { hours, date, company, unpaidBreakSeconds, dayElapsedHours, ts, defaultClockInTime } = inputs;
  const zone = company?.timezone;
  const [year, month, day] = date.split("-");
  if (!year || !month || !day) throw Error(`There was an error saving the timesheets.`);
  const clockInTime = defaultClockInTime || company?.settings.timesheets.default_clock_in_time || "09:00";
  const [hour, minute] = clockInTime.split(":");
  if (!hour || !minute) throw Error(`There was an error saving the timesheets.`);
  const clockInSeconds =
    ts?.clock_in ||
    DateTime.fromObject(
      {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
      },
      { zone }
    )
      .plus({ hours: dayElapsedHours })
      .toSeconds();
  return {
    clock_in: clockInSeconds,
    clock_out: clockInSeconds + hours * SECONDS_PER_HOUR + (unpaidBreakSeconds || 0),
  };
};
